import { Action } from '@ngrx/store';
import { EntityState, createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { RuleEntry, RuleFilterInput } from 'app/api/rule.api.service';
import {
    RuleActionTypes,
    RuleLoaded,
    RuleRequested,
    RuleCancelled,
    RuleEntryUpdateCancelled,
    RuleEntryUpdated,
    RuleEntryCreated,
    RuleEntryCreateCancelled,
    RuleEntryDeleted,
    RuleEntryDeleteCancelled,
} from './rule.actions';
import { KeyStringFilterInput } from 'app/api/common.api.types';
import { LoadingState, CallState } from 'app/utils/callstate';

// tslint:disable-next-line: no-empty-interface
export interface RuleState extends EntityState<RuleEntry> {
    companyId: string;
    key?: KeyStringFilterInput;
    filter?: RuleFilterInput;
    callState: CallState;
}

export const adapter: EntityAdapter<RuleEntry> = createEntityAdapter<
    RuleEntry
>({
    selectId: entry => entry.key
});
export const initialRuleState: RuleState = adapter.getInitialState({
    companyId: undefined,
    key: undefined,
    filter: undefined,
    callState: LoadingState.Init
});

export function reducer(
    state = initialRuleState,
    action: Action
): RuleState {
    switch (action.type) {
        case RuleActionTypes.RuleRequested:
            const {
                companyId,
                key,
                filter
            } = (action as RuleRequested).payload;
            return {
                ...state,
                companyId,
                key,
                filter,
                callState: LoadingState.Loading
            };
        case RuleActionTypes.RuleLoaded:
                return adapter.addAll((action as RuleLoaded).payload.rule, {
                ...state,
                callState: LoadingState.Loaded
            });
        case RuleActionTypes.RuleCancelled:
            return {
                ...state,
                callState: {
                    errorMessage: (action as RuleCancelled).payload
                        .errorMessage
                }
            };
        case RuleActionTypes.RuleEntryUpdateRequested:
            return {
                ...state,
                callState: LoadingState.Loading
            };
        case RuleActionTypes.RuleEntryUpdated:
            const entry = (action as RuleEntryUpdated).payload.entry;
            return adapter.updateOne({id: entry.key, changes: entry}, {
                ...state,
                callState: LoadingState.Loaded
            });
        case RuleActionTypes.RuleEntryUpdateCancelled:
            return {
                ...state,
                callState: {
                    errorMessage: (action as RuleEntryUpdateCancelled).payload
                        .errorMessage
                }
            };
        case RuleActionTypes.RuleEntryCreateRequested:
            return {
                ...state,
                callState: LoadingState.Loading
            };
        case RuleActionTypes.RuleEntryCreated:
            const newEntry = (action as RuleEntryCreated).payload.entry;
            return adapter.addOne(newEntry, {
                ...state,
                callState: LoadingState.Loaded
            });
        case RuleActionTypes.RuleEntryCreateCancelled:
            return {
                ...state,
                callState: {
                    errorMessage: (action as RuleEntryCreateCancelled).payload
                        .errorMessage
                }
            };
        case RuleActionTypes.RuleEntryDeleteRequested:
            return {
                ...state,
                callState: LoadingState.Loading
            };
        case RuleActionTypes.RuleEntryDeleted:
            const deletedEntry = (action as RuleEntryDeleted).payload.entry;
            return adapter.removeOne(deletedEntry.key, {
                ...state,
                callState: LoadingState.Loaded
            });
        case RuleActionTypes.RuleEntryDeleteCancelled:
            return {
                ...state,
                callState: {
                    errorMessage: (action as RuleEntryDeleteCancelled).payload
                        .errorMessage
                }
            };
        default:
            return state;
    }
}

export const {
    selectAll,
    selectEntities,
    selectIds,
    selectTotal
} = adapter.getSelectors();
